import { useBehavior } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity } from 'react';
import { TReferralsProgressData } from '../../types/data';
import { TTextOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './referralsProgress.module.css';

export const ReferralsProgress: React.FC<TWidgetProps<TReferralsProgressData>> = ({ widget }) => {
    const { earnedAmount = 0, totalAmount = 0, totalSteps = 0 } = widget.data?.value ?? {};
    const { referralTitle, startSubtitle, progressSubtitle } = widget.data?.messages ?? {};
    const { margin } = widget.options ?? {};
    const { isHidden } = useBehavior(widget);

    const completedSteps = totalSteps > 0 ? Math.floor((earnedAmount / totalAmount) * totalSteps) : 0;
    const hasProgress = earnedAmount > 0;
    const fillPercent = totalSteps > 1 && completedSteps > 0 ? ((completedSteps - 1) / (totalSteps - 1)) * 100 : 0;

    const amountColor = hasProgress ? 'purple_100' : 'grey_72_lc';
    const amountOptions: TTextOption = { variant: 'main', textColor: amountColor, textAlign: 'end' };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div className={styles.container} style={getInlineStyles({ margin })} data-test={widget.uid}>
                <div className={styles.header}>
                    {referralTitle && <TextCommon isWidget={false} content={referralTitle} />}
                    {progressSubtitle && <TextCommon isWidget={false} options={amountOptions} content={progressSubtitle} />}
                </div>
                <div className={styles.progressTrack} data-test="progress-track">
                    <div className={styles.progressFill} data-test="progress-fill" style={{ width: `${fillPercent}%` }} />
                    {Array.from({ length: totalSteps }, (_, i) => (
                        <div
                            key={i}
                            data-test={i < completedSteps ? 'dot-active' : 'dot-inactive'}
                            className={clsx(styles.dot, i < completedSteps ? styles.dotActive : styles.dotInactive)}
                        />
                    ))}
                </div>
                {startSubtitle && <TextCommon isWidget={false} content={startSubtitle} options={{ variant: 'secondary' }} />}
            </div>
        </Activity>
    );
};
